import JSZip from "jszip";

export interface ParsedPdf {
	orgId: string;
	pdfType: string;
	clientName: string;
	settingsPart: string;
	blob: Blob;
	filename: string;
}

/**
 * Parses a ZIP file and extracts PDFs with their organization ID, client name, type, and settings
 * Supports two formats:
 * - New: OrgName/ClientName_TYPE_settings_date.pdf
 * - Legacy: uuid/TYPE_date.pdf
 */
export const parseZipFile = async (zipFile: File): Promise<ParsedPdf[]> => {
	const zip = new JSZip();
	const zipData = await zip.loadAsync(zipFile);
	const pdfs: ParsedPdf[] = [];

	// Iterate through all files in the ZIP
	for (const [path, file] of Object.entries(zipData.files)) {
		// Skip directories
		if (file.dir) continue;

		// Skip non-PDF files
		if (!path.toLowerCase().endsWith('.pdf')) continue;

		// Parse the path: OrgName/ClientName_TYPE_settings_date.pdf
		const parts = path.split('/');
		if (parts.length !== 2) {
			console.warn(`Unexpected path format: ${path}`);
			continue;
		}

		const orgId = parts[0];
		const filename = parts[1];

		// Get the file content as blob
		const blob = await file.async('blob');

		// Parse filename - handle both new and old formats
		const filenameWithoutExt = filename.replace(/\.pdf$/i, '');

		// Try to parse new format: ClientName_TYPE_settings_date
		// Pattern: anything_TYPE_anything_date
		// We know TYPE is uppercase abbreviations like SRG, RG, LS, KV, etc.

		// Split by underscore
		const filenameParts = filenameWithoutExt.split('_');

		if (filenameParts.length >= 3) {
			// New format: ClientName_TYPE_settings_date or ClientName_TYPE_date
			// We need to find the TYPE part (uppercase abbreviations)
			// Common types: SRG, RG, LS, KV, AKONTO, GS, etc.

			let typeIndex = -1;
			for (let i = 1; i < filenameParts.length; i++) {
				const part = filenameParts[i];
				// Check if this looks like a PDF type (all uppercase, common abbreviations)
				if (/^[A-Z]+$/.test(part)) {
					typeIndex = i;
					break;
				}
			}

			if (typeIndex > 0) {
				// Extract components
				const clientName = filenameParts.slice(0, typeIndex).join('_');
				const pdfType = filenameParts[typeIndex];

				// Everything between TYPE and the date (last part) is settings
				// The last part is the date in format YYYY-MM-DD
				const lastPart = filenameParts[filenameParts.length - 1];
				const isDate = /^\d{4}-\d{2}-\d{2}$/.test(lastPart);

				let settingsPart = '';
				if (isDate && typeIndex < filenameParts.length - 1) {
					// Settings are between TYPE and date
					settingsPart = filenameParts.slice(typeIndex + 1, filenameParts.length - 1).join('_');
				} else if (!isDate) {
					// No date at end, everything after TYPE is settings
					settingsPart = filenameParts.slice(typeIndex + 1).join('_');
				}

				pdfs.push({
					orgId,
					pdfType,
					clientName,
					settingsPart,
					blob,
					filename,
				});
			} else {
				// Fallback: old format TYPE_date.pdf
				const pdfType = filenameParts[0];
				pdfs.push({
					orgId,
					pdfType,
					clientName: 'Unknown',
					settingsPart: '',
					blob,
					filename,
				});
			}
		} else {
			// Old format: TYPE_date.pdf or just TYPE.pdf
			const pdfType = filenameParts[0];
			pdfs.push({
				orgId,
				pdfType,
				clientName: 'Unknown',
				settingsPart: '',
				blob,
				filename,
			});
		}
	}

	return pdfs;
};
